import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { assessmentSessions } from '../api/client';

export default function AssessmentResultsPage() {
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState(null);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(null);

  useEffect(() => {
    loadResults();
  }, [id]);

  const loadResults = async () => {
    try {
      const [sessionRes, resultsRes] = await Promise.all([
        assessmentSessions.get(id),
        assessmentSessions.results(id),
      ]);
      setSession(sessionRes.data);
      setResults(resultsRes.data);
    } catch (err) {
      console.error('Failed to load results:', err);
      setError(err.response?.data?.error || 'Failed to load results');
    } finally {
      setLoading(false);
    }
  };

  const handleExportPdf = async () => {
    setExporting('pdf');
    try {
      const response = await assessmentSessions.exportPdf(id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `assessment-report-${id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to export PDF:', err);
      alert('Failed to export PDF. Please try again.');
    } finally {
      setExporting(null);
    }
  };

  const handleExportCsv = async () => {
    setExporting('csv');
    try {
      const response = await assessmentSessions.exportCsv(id);
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `assessment-report-${id}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to export CSV:', err);
      alert('Failed to export CSV. Please try again.');
    } finally {
      setExporting(null);
    }
  };

  const getBenchmarkColor = (level) => {
    const colors = {
      exceeding: { bg: 'bg-green-100', text: 'text-green-800', bar: 'bg-green-500' },
      meeting: { bg: 'bg-blue-100', text: 'text-blue-800', bar: 'bg-blue-500' },
      approaching: { bg: 'bg-yellow-100', text: 'text-yellow-800', bar: 'bg-yellow-500' },
      below: { bg: 'bg-red-100', text: 'text-red-800', bar: 'bg-red-500' },
    };
    return colors[level] || colors.below;
  };

  const getMasteryColor = (level) => {
    const colors = {
      mastered: { bg: 'bg-green-100', text: 'text-green-800', bar: 'bg-green-500', border: 'border-green-300' },
      developing: { bg: 'bg-blue-100', text: 'text-blue-800', bar: 'bg-blue-500', border: 'border-blue-300' },
      emerging: { bg: 'bg-yellow-100', text: 'text-yellow-800', bar: 'bg-yellow-500', border: 'border-yellow-300' },
      not_demonstrated: { bg: 'bg-red-100', text: 'text-red-800', bar: 'bg-red-500', border: 'border-red-300' },
    };
    return colors[level] || colors.not_demonstrated;
  };

  const getBenchmarkIcon = (level) => {
    const icons = {
      exceeding: (
        <svg className="w-6 h-6 text-green-500" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
        </svg>
      ),
      meeting: (
        <svg className="w-6 h-6 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v3.586L7.707 9.293a1 1 0 00-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 10.586V7z" clipRule="evenodd" />
        </svg>
      ),
      approaching: (
        <svg className="w-6 h-6 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
        </svg>
      ),
      below: (
        <svg className="w-6 h-6 text-red-500" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
        </svg>
      ),
    };
    return icons[level] || icons.below;
  };

  if (loading) {
    return (
      <div className="px-4 py-6 sm:px-0">
        <div className="text-gray-500">Loading results...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-4 py-6 sm:px-0">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <p className="text-red-600">{error}</p>
          <Link
            to={`/app/assessments/${id}`}
            className="mt-4 inline-block text-indigo-600 hover:text-indigo-500"
          >
            &larr; Back to Assessment
          </Link>
        </div>
      </div>
    );
  }

  if (!session || !results) {
    return (
      <div className="px-4 py-6 sm:px-0">
        <div className="text-gray-500">No results available</div>
      </div>
    );
  }

  const overallColors = getBenchmarkColor(results.overall?.benchmark_level);

  return (
    <div className="px-4 py-6 sm:px-0">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <Link
            to={`/app/students/${session.student_id}/assessments`}
            className="text-sm text-indigo-600 hover:text-indigo-500"
          >
            &larr; Back to Assessments
          </Link>
          <div className="flex space-x-2">
            <button
              onClick={handleExportPdf}
              disabled={exporting === 'pdf'}
              className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {exporting === 'pdf' ? (
                <>
                  <svg className="animate-spin -ml-0.5 mr-2 h-4 w-4 text-gray-500" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Exporting...
                </>
              ) : (
                <>
                  <svg className="-ml-0.5 mr-2 h-4 w-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  PDF
                </>
              )}
            </button>
            <button
              onClick={handleExportCsv}
              disabled={exporting === 'csv'}
              className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {exporting === 'csv' ? (
                <>
                  <svg className="animate-spin -ml-0.5 mr-2 h-4 w-4 text-gray-500" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Exporting...
                </>
              ) : (
                <>
                  <svg className="-ml-0.5 mr-2 h-4 w-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  CSV
                </>
              )}
            </button>
          </div>
        </div>
        <h1 className="mt-2 text-2xl font-semibold text-gray-900">
          {session.template.name} Results
        </h1>
        <p className="mt-1 text-sm text-gray-500">
          Completed on {new Date(session.completed_at).toLocaleDateString()}
        </p>
      </div>

      {/* Overall Score Card */}
      {results.overall && (
        <div className={`rounded-lg p-6 mb-8 ${overallColors.bg}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              {getBenchmarkIcon(results.overall.benchmark_level)}
              <div>
                <h2 className={`text-2xl font-bold ${overallColors.text}`}>
                  {results.overall.benchmark_label}
                </h2>
                <p className="text-sm opacity-75">
                  Overall Score: {results.overall.percentage}%
                </p>
              </div>
            </div>
            <div className="text-right">
              <div className={`text-4xl font-bold ${overallColors.text}`}>
                {results.overall.score}/{results.overall.max_score}
              </div>
              <p className="text-sm opacity-75">points earned</p>
            </div>
          </div>

          {/* Score Bar */}
          <div className="mt-4">
            <div className="w-full bg-white bg-opacity-50 rounded-full h-3">
              <div
                className={`h-3 rounded-full ${overallColors.bar}`}
                style={{ width: `${results.overall.percentage}%` }}
              />
            </div>
          </div>

          {/* Recommendations */}
          {results.overall.recommendations && results.overall.recommendations.length > 0 && (
            <div className="mt-4 pt-4 border-t border-black border-opacity-10">
              <h3 className="text-sm font-medium mb-2">Recommendations:</h3>
              <ul className="list-disc list-inside text-sm space-y-1">
                {results.overall.recommendations.map((rec, idx) => (
                  <li key={idx}>{rec.message}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Section Results */}
      <div className="mb-8">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Results by Section</h2>
        <div className="space-y-4">
          {results.sections?.map((section) => {
            const colors = getBenchmarkColor(section.benchmark_level);
            return (
              <div key={section.section_id} className="bg-white rounded-lg shadow p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    {getBenchmarkIcon(section.benchmark_level)}
                    <div>
                      <h3 className="text-md font-medium text-gray-900">
                        {section.section_name}
                      </h3>
                      <span className={`text-sm px-2 py-0.5 rounded ${colors.bg} ${colors.text}`}>
                        {section.benchmark_label}
                      </span>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-semibold text-gray-900">
                      {section.percentage}%
                    </div>
                    <p className="text-xs text-gray-500">
                      {section.score}/{section.max_score} pts
                    </p>
                  </div>
                </div>

                {/* Section Progress Bar */}
                <div className="w-full bg-gray-200 rounded-full h-2 mb-3">
                  <div
                    className={`h-2 rounded-full ${colors.bar}`}
                    style={{ width: `${section.percentage}%` }}
                  />
                </div>

                {/* Section Recommendations */}
                {section.recommendations && section.recommendations.length > 0 && (
                  <div className="text-sm text-gray-600 bg-gray-50 rounded p-3">
                    <p className="font-medium mb-1">Areas to focus on:</p>
                    <ul className="list-disc list-inside space-y-1">
                      {section.recommendations.map((rec, idx) => (
                        <li key={idx}>{rec.message}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Learning Gaps Report */}
      {results.learning_gaps && results.learning_gaps.length > 0 && (
        <div className="mb-8">
          <h2 className="text-lg font-medium text-gray-900 mb-2">Learning Gaps</h2>
          <p className="text-sm text-gray-500 mb-4">
            These skills need attention. Focus on these areas for improvement.
          </p>
          <div className="space-y-4">
            {results.learning_gaps.map((gap) => {
              const colors = getMasteryColor(gap.mastery_level);
              return (
                <div key={gap.skill_id} className={`bg-white rounded-lg shadow border-l-4 ${colors.border} p-4`}>
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h3 className="font-medium text-gray-900">{gap.skill_name}</h3>
                      <p className="text-sm text-gray-500">{gap.description}</p>
                      <div className="mt-1 flex items-center space-x-2">
                        <span className="text-xs text-gray-400">{gap.skill_code}</span>
                        <span className="text-xs text-gray-400">|</span>
                        <span className="text-xs text-gray-400 capitalize">{gap.category}</span>
                        <span className="text-xs text-gray-400">|</span>
                        <span className="text-xs text-gray-400">Grade {gap.grade_level}</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className={`inline-block px-2 py-1 text-xs rounded ${colors.bg} ${colors.text}`}>
                        {gap.mastery_label}
                      </span>
                      <div className="text-sm font-medium text-gray-700 mt-1">
                        {gap.percentage}%
                      </div>
                    </div>
                  </div>

                  {/* Prerequisites that might need review */}
                  {gap.prerequisites && gap.prerequisites.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-gray-100">
                      <p className="text-xs font-medium text-gray-500 mb-2">Related prerequisite skills:</p>
                      <div className="flex flex-wrap gap-2">
                        {gap.prerequisites.map((prereq) => {
                          const prereqColors = prereq.mastery_level ? getMasteryColor(prereq.mastery_level) : { bg: 'bg-gray-100', text: 'text-gray-600' };
                          return (
                            <span
                              key={prereq.skill_id}
                              className={`inline-flex items-center px-2 py-1 text-xs rounded ${prereqColors.bg} ${prereqColors.text}`}
                            >
                              {prereq.skill_name}
                              {prereq.percentage !== null && prereq.percentage !== undefined && (
                                <span className="ml-1 opacity-75">({prereq.percentage}%)</span>
                              )}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Skill Mastery Breakdown */}
      {results.skills && results.skills.length > 0 && (
        <div className="mb-8">
          <h2 className="text-lg font-medium text-gray-900 mb-2">Skill Mastery Breakdown</h2>
          <p className="text-sm text-gray-500 mb-4">
            Individual skill performance across this assessment.
          </p>

          {/* Group skills by category */}
          {['ela', 'math'].map((category) => {
            const categorySkills = results.skills.filter(s => s.category === category);
            if (categorySkills.length === 0) return null;

            return (
              <div key={category} className="mb-6">
                <h3 className="text-md font-medium text-gray-700 mb-3 capitalize">
                  {category === 'ela' ? 'English Language Arts' : 'Mathematics'}
                </h3>
                <div className="bg-white rounded-lg shadow overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Skill</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Grade</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Score</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Mastery</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {categorySkills.map((skill) => {
                        const colors = getMasteryColor(skill.mastery_level);
                        return (
                          <tr key={skill.skill_id}>
                            <td className="px-4 py-3">
                              <div className="text-sm font-medium text-gray-900">{skill.skill_name}</div>
                              <div className="text-xs text-gray-400">{skill.skill_code}</div>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-500">{skill.grade_level}</td>
                            <td className="px-4 py-3">
                              <div className="flex items-center">
                                <div className="w-20 bg-gray-200 rounded-full h-2 mr-2">
                                  <div
                                    className={`h-2 rounded-full ${colors.bar}`}
                                    style={{ width: `${skill.percentage}%` }}
                                  />
                                </div>
                                <span className="text-sm text-gray-600">{skill.percentage}%</span>
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <span className={`inline-block px-2 py-1 text-xs rounded ${colors.bg} ${colors.text}`}>
                                {skill.mastery_label}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Score Legend */}
      <div className="bg-gray-50 rounded-lg p-4 mb-4">
        <h3 className="text-sm font-medium text-gray-900 mb-3">Section Score Legend</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 rounded-full bg-green-500" />
            <span className="text-sm text-gray-600">85%+ Exceeding</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 rounded-full bg-blue-500" />
            <span className="text-sm text-gray-600">70-84% Meeting</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 rounded-full bg-yellow-500" />
            <span className="text-sm text-gray-600">50-69% Approaching</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 rounded-full bg-red-500" />
            <span className="text-sm text-gray-600">&lt;50% Below</span>
          </div>
        </div>
      </div>

      {/* Skill Mastery Legend */}
      {results.skills && results.skills.length > 0 && (
        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="text-sm font-medium text-gray-900 mb-3">Skill Mastery Legend</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 rounded-full bg-green-500" />
              <span className="text-sm text-gray-600">75%+ Mastered</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 rounded-full bg-blue-500" />
              <span className="text-sm text-gray-600">50-74% Developing</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 rounded-full bg-yellow-500" />
              <span className="text-sm text-gray-600">25-49% Emerging</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 rounded-full bg-red-500" />
              <span className="text-sm text-gray-600">&lt;25% Not Demonstrated</span>
            </div>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="mt-8 flex justify-center space-x-4">
        <Link
          to={`/app/students/${session.student_id}`}
          className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
        >
          Back to Student
        </Link>
        <Link
          to={`/app/students/${session.student_id}/assessments`}
          className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
        >
          Take Another Assessment
        </Link>
      </div>
    </div>
  );
}
